/**
 * Atomic Silent Refresh Interceptor (axios response interceptor).
 *
 * What it does
 * ============
 * On any 401 from a protected API call, this transparently:
 *   1. Pauses the failed request.
 *   2. Calls POST `/api/auth/refresh` ONCE — even if 10 concurrent requests
 *      all 401 at the same time (atomic queueing on `isRefreshing`).
 *   3. On refresh success, replays the original request (plus all queued
 *      requests) with the new session cookies. Caller sees no error.
 *   4. On refresh failure (7-day refresh window exhausted, garbage token,
 *      user deleted, etc.) — rejects everything and hard-redirects the host
 *      to /login. Guests on public landing pages are NOT redirected.
 *
 * Why one shared module
 * =====================
 * The whole codebase uses the global `axios` default (no per-call instance),
 * with `axios.defaults.withCredentials = true` set in AuthContext. Installing
 * this interceptor once at app boot wires every existing call site for free —
 * no per-component changes required.
 *
 * Design choices worth knowing
 * ============================
 * - `_authRetried` is sentinelled on the request config so each request can
 *   only attempt a single refresh-and-retry. Eliminates infinite recursion.
 * - The auth-endpoint allowlist (`/auth/refresh`, `/auth/login`, `/auth/register`)
 *   short-circuits the interceptor for the auth ceremony itself. Their 401s
 *   propagate to the caller (the login form needs to see "wrong password").
 * - We DO want to refresh on `/auth/me` 401s — that's the canonical "access
 *   token expired" symptom for an otherwise valid session.
 * - The redirect-to-/login fallback is gated on a public-path matcher so the
 *   guest QR experience never gets bounced when their browser has no session
 *   (the expected case for guests).
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public routes where a 401 from /auth/me (no session) is the EXPECTED state —
// don't yank the guest off their landing page. Each pattern below corresponds
// to a public <Route> in App.js (see lines 187-195).
const PUBLIC_PATH_PATTERNS = [
  /^\/$/,                          // root marketing page
  /^\/login\b/,
  /^\/register\b/,
  /^\/privacy\b/,                   // public legal — /privacy
  /^\/terms\b/,                     // public legal — /terms
  /^\/radio\b/,                     // public MusicBooth Radio (+ /radio#world deep links)
  // Single-prefix guest pages
  /^\/qr\//,                        // legacy guest QR landing
  /^\/tv\b/,                        // /tv/{code}  (matches /tv, /tv/, /tv/XYZ)
  /^\/g\//,                         // any other public guest sub-path
  /^\/request\//,                   // /request/{code}
  /^\/v\//,                         // /v/{venueCode} — venue landing
  /^\/menu\//,                      // /menu/{code}
  /^\/review\//,                    // /review/{code}
  /^\/play\//,                      // /play/{code} — guest games
  /^\/event\//,                     // /event/{shareCode} — public event RSVP
  /^\/staff\b/,                     // legacy team-lead mobile mode
  /^\/dj-handoff\b/,                // new DJ Hand-off PWA URL — same auth model
  // Venue-prefixed permanent TV URL: /{venueCode}/tv  (with optional trailing slash)
  // This is what venues type into smart TVs — must NEVER bounce to /login.
  /^\/[^/]+\/tv\/?$/,
];

const isPublicPath = (pathname) => PUBLIC_PATH_PATTERNS.some((re) => re.test(pathname || '/'));

// Endpoints that MUST NOT trigger a silent refresh — refreshing on these
// either loops forever or hides a UX-relevant 401 from the user.
const AUTH_CEREMONY_PATHS = [
  '/auth/refresh',
  '/auth/login',
  '/auth/register',
  // Public guest OTP routes — a 401 here means "verification expired",
  // NOT "your access cookie has aged out". Surface the 401 to the caller.
  '/otp/send',
  '/otp/verify',
  // Guest song-request submit returns 401 when the OTP token has expired;
  // re-running /auth/refresh would not help (guest isn't logged in).
  '/submit',
  // Feb 2026 (production stall RCA): /playback/ping is a low-signal
  // 3-second heartbeat. A single stray 401 here shouldn't trigger a
  // refresh cascade (each refresh takes ~200ms; if refresh itself 401s
  // the venue gets bounced to /login mid-set). Let pings fail silently
  // — the next successful heartbeat picks up state, and any user-facing
  // 401 (queue/next, playlists/current) will still trigger the normal
  // refresh flow.
  '/playback/ping',
];
const isAuthCeremony = (url) => {
  const u = url || '';
  return AUTH_CEREMONY_PATHS.some((p) => u.includes(p));
};

// Module-level coordination state. `isRefreshing` ensures atomicity across
// concurrent 401s; `pendingQueue` holds the resolvers for every request
// waiting on the in-flight refresh.
let isRefreshing = false;
let pendingQueue = [];

const flushQueue = (err) => {
  const q = pendingQueue;
  pendingQueue = [];
  q.forEach(({ resolve, reject }) => (err ? reject(err) : resolve()));
};

const redirectToLogin = () => {
  if (typeof window === 'undefined') return;
  // Don't yank guests off public landings, and don't loop if we're already there.
  const path = window.location.pathname;
  if (isPublicPath(path)) return;
  // ===== Feb 2026 fix (production stall RCA 86a0542b) =====
  // If MusicKit is actively playing, a hard navigation to /login kills
  // playback — this was the 15-30 min stall pattern: an intermittent
  // /auth/refresh 401 (mobile Safari cookie edge case) yanked the tab
  // off /dashboard while a set was live. Instead, defer the redirect
  // and surface a persistent toast so the user re-auths manually
  // AFTER their set winds down.
  try {
    const isPlaying =
      (typeof window.MusicKit !== 'undefined' &&
        window.MusicKit.getInstance &&
        window.MusicKit.getInstance().playbackState === 2);
    if (isPlaying) {
      console.warn('[auth] Refresh 401 while playing — deferring redirect. Persistent toast will surface re-auth prompt.');
      // Best-effort toast — module has no direct sonner import; dispatch
      // a global custom event that any layout listener can pick up.
      try {
        window.dispatchEvent(new CustomEvent('musicbox:auth-lost', {
          detail: { at: new Date().toISOString() },
        }));
      } catch { /* noop */ }
      return;
    }
  } catch { /* fall through to redirect on any probing error */ }
  // Use replace() so the broken page isn't reachable via the back button.
  window.location.replace('/login');
};

export const installAuthInterceptor = () => {
  axios.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      const original = error?.config;
      const status = error?.response?.status;

      // Pass through anything that isn't a 401 we can/should handle.
      if (status !== 401 || !original) return Promise.reject(error);
      if (original._authRetried) return Promise.reject(error);
      if (isAuthCeremony(original.url)) return Promise.reject(error);

      // Mark — guarantees each request gets at most one refresh-and-retry.
      original._authRetried = true;

      // Concurrent 401 path: piggy-back on the refresh that's already in flight.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({
            resolve: () => resolve(axios(original)),
            reject,
          });
        });
      }

      // We're the first 401 of the wave — own the refresh.
      isRefreshing = true;
      try {
        await axios.post(`${API}/auth/refresh`);
        flushQueue(null);     // replay every queued request
        return axios(original); // and our own
      } catch (refreshErr) {
        // Refresh itself returned 401 (or network died). Refresh-token window
        // is over — hard logout.
        flushQueue(refreshErr);
        redirectToLogin();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    },
  );
};

export default installAuthInterceptor;
